import { usePhysicianStore } from '@/entities/physician';
import { useSupportStore } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { emitter, GETTEXT_ALL_FACILITIES } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect } from 'react';
import { facilitySelection } from '@/entities/facility';
import { handleFormatSupportFacilites } from '../lib/titleComponent';

/**
 * Backs the page header's facility and see-patients pickers. Reads the support facilities, the selected facility and the see-patients mode plus their store actions, reformats the facilities into sorted options whenever the support list changes, and exposes onChange, which persists a facility, emits its arrears escalation and reloads its physicians, and onChangeTypeSeePatient.
 *
 * @returns The formatted facility options, the current selections and both change handlers.
 */
export const useTitleComponent = () => {
  /**
   * The support facilities taken from the support store. Feed the title-bar facility options and the lookup that reads a facility arrears value.
   *
   * @returns The support facility list held in the support store.
   */
  const supportFacilitiesRedux = useSupportStore((s) => s.supportFacilities);
  /**
   * The currently selected facility taken from the user store. Backs the value of the title-bar facility picker and the equality check that skips redundant facility changes.
   *
   * @returns The selected facility option held in the user store.
   */
  const selectedFacilityRedux = useUserStore((s) => s.selectedFacility);
  /**
   * The currently selected see-patients mode taken from the user store. Backs the value of the title-bar mode picker.
   *
   * @returns The selected see-patients option held in the user store.
   */
  const selectedTypeSeePatients = useUserStore((s) => s.selectedTypeSeePatients);

  /**
   * The user store's changeSelectedFacility action, read through a useUserStore selector. Publishes a newly picked facility into the global session state.
   *
   * @returns The changeSelectedFacility action from the user store.
   */
  const changeSelectedFacilityRequestAction = useUserStore((s) => s.changeSelectedFacility);
  /**
   * The user store's changeSelectedTypeSeePatients action, read through a useUserStore selector. Publishes a newly picked see-patients mode into the global session state.
   *
   * @returns The changeSelectedTypeSeePatients action from the user store.
   */
  const changeSelectedTypeSeePatientsAction = useUserStore((s) => s.changeSelectedTypeSeePatients);
  /**
   * The physician store's fetchPhysicians action, read through a usePhysicianStore selector. It reloads the physician list for a facility and discards responses from superseded requests.
   *
   * @returns The fetchPhysicians action from the physician store.
   */
  const fetchPhysiciansRequestAction = usePhysicianStore((s) => s.fetchPhysicians);

  const [state, setState] = useMergeState<{
    formattedSupportFacilities: ReturnType<typeof handleFormatSupportFacilites>;
  }>({
    formattedSupportFacilities: [],
  });

  /**
   * Applies the see-patients mode picked in the page header. Persists the option to local storage and forwards it to the user store's changeSelectedTypeSeePatients action; the name and value arguments are unused.
   *
   * @param name - The field name reported by the picker, unused.
   * @param value - The raw value reported by the picker, unused.
   * @param option - The see-patients option that was selected.
   * @returns Nothing.
   */
  const onChangeTypeSeePatient = (name: any, value: any, option: any) => {
    facilitySelection.setSelectedTypeSeePatientsLocalStorage(option);
    changeSelectedTypeSeePatientsAction(option);
  };

  /**
   * Switches the active facility from the page-header picker. Ignores a selection equal to the current facility, emits the escalation popup with the matched facility's arrears, persists the option to local storage, reloads physicians for that facility, passing undefined for the all-facilities sentinel, then writes the selection into the user store.
   *
   * @param name - The field name reported by the picker, unused.
   * @param value - The raw value reported by the picker, unused.
   * @param option - The facility option that was selected.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any, option: any) => {
    if (option.value !== selectedFacilityRedux.value) {
      /**
       * The raw support facility whose id matches the newly picked option. Located with a lodash find over the support facilities held in the store and supplies the arrears payload for the escalation popup.
       *
       * @returns The matching support facility, or undefined when none matches.
       */
      const foundSelectedFacility = _.find(supportFacilitiesRedux, (x) => x.id === option.value);
      if (foundSelectedFacility) {
        emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, foundSelectedFacility.arrears);
      }

      const selectedFacilityId = option.value !== GETTEXT_ALL_FACILITIES ? option.value : undefined;
      facilitySelection.setSelectedFacilityLocalStorage(option);
      fetchPhysiciansRequestAction(selectedFacilityId);
      changeSelectedFacilityRequestAction(option);
    }
  };

  useEffect(() => {
    if (supportFacilitiesRedux?.length) {
      const formattedSupportFacilities = handleFormatSupportFacilites(supportFacilitiesRedux);
      setState({ formattedSupportFacilities });
    }
  }, [supportFacilitiesRedux]);

  return {
    state,
    selectedFacilityRedux,
    selectedTypeSeePatients,
    onChange,
    onChangeTypeSeePatient,
  };
};
